/**
 * Video Performance Analytics System
 * Comprehensive tracking and measurement of video loading performance
 */

export interface VideoPerformanceMetrics {
  videoId: string;
  timestamp: number;
  loadStartTime: number;
  thumbnailLoadTime: number;
  iframeLoadTime: number;
  totalLoadTime: number;
  connectionType: string;
  connectionSpeed: number;
  thumbnailQuality: string;
  cacheHit: boolean;
  userAgent: string;
  viewportSize: { width: number; height: number };
  deviceType: "mobile" | "tablet" | "desktop";
  optimizationEnabled: boolean;
  errors: string[];
}

export interface PerformanceReport {
  summary: {
    totalVideos: number;
    averageLoadTime: number;
    averageThumbnailLoadTime: number;
    averageIframeLoadTime: number;
    cacheHitRate: number;
    errorRate: number;
  };
  byConnectionType: Record<
    string,
    {
      count: number;
      averageLoadTime: number;
      averageThumbnailLoadTime: number;
    }
  >;
  byDeviceType: Record<
    string,
    {
      count: number;
      averageLoadTime: number;
    }
  >;
  byQuality: Record<
    string,
    {
      count: number;
      averageLoadTime: number;
    }
  >;
  recentMetrics: VideoPerformanceMetrics[];
}

export class VideoPerformanceAnalytics {
  private static instance: VideoPerformanceAnalytics;
  private metrics: VideoPerformanceMetrics[] = [];
  private maxMetrics = 1000; // Keep last 1000 metrics
  private analyticsEndpoint: string | null = null;

  static getInstance(): VideoPerformanceAnalytics {
    if (!VideoPerformanceAnalytics.instance) {
      VideoPerformanceAnalytics.instance = new VideoPerformanceAnalytics();
    }
    return VideoPerformanceAnalytics.instance;
  }

  /**
   * Set analytics endpoint for remote tracking
   */
  setAnalyticsEndpoint(endpoint: string): void {
    this.analyticsEndpoint = endpoint;
  }

  /**
   * Record video performance metrics
   */
  recordMetrics(metrics: Omit<VideoPerformanceMetrics, "timestamp">): void {
    const fullMetrics: VideoPerformanceMetrics = {
      ...metrics,
      timestamp: Date.now(),
    };

    this.metrics.push(fullMetrics);

    // Keep only the last maxMetrics entries
    if (this.metrics.length > this.maxMetrics) {
      this.metrics = this.metrics.slice(-this.maxMetrics);
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.log("📊 Video Performance Metrics:", {
        videoId: fullMetrics.videoId,
        totalLoadTime: `${fullMetrics.totalLoadTime.toFixed(2)}ms`,
        thumbnailLoadTime: `${fullMetrics.thumbnailLoadTime.toFixed(2)}ms`,
        iframeLoadTime: `${fullMetrics.iframeLoadTime.toFixed(2)}ms`,
        connectionType: fullMetrics.connectionType,
        cacheHit: fullMetrics.cacheHit,
        optimizationEnabled: fullMetrics.optimizationEnabled,
      });
    }

    // Send to analytics endpoint if configured
    this.sendToAnalytics(fullMetrics);
  }

  /**
   * Get comprehensive performance report
   */
  getPerformanceReport(): PerformanceReport {
    if (this.metrics.length === 0) {
      return {
        summary: {
          totalVideos: 0,
          averageLoadTime: 0,
          averageThumbnailLoadTime: 0,
          averageIframeLoadTime: 0,
          cacheHitRate: 0,
          errorRate: 0,
        },
        byConnectionType: {},
        byDeviceType: {},
        byQuality: {},
        recentMetrics: [],
      };
    }

    const totalVideos = this.metrics.length;
    const averageLoadTime =
      this.metrics.reduce((sum, m) => sum + m.totalLoadTime, 0) / totalVideos;
    const averageThumbnailLoadTime =
      this.metrics.reduce((sum, m) => sum + m.thumbnailLoadTime, 0) /
      totalVideos;
    const averageIframeLoadTime =
      this.metrics.reduce((sum, m) => sum + m.iframeLoadTime, 0) / totalVideos;
    const cacheHitRate =
      this.metrics.filter((m) => m.cacheHit).length / totalVideos;
    const errorRate =
      this.metrics.filter((m) => m.errors.length > 0).length / totalVideos;

    // Group by connection type
    const byConnectionType: Record<string, any> = {};
    this.metrics.forEach((metric) => {
      const type = metric.connectionType;
      if (!byConnectionType[type]) {
        byConnectionType[type] = {
          count: 0,
          totalLoadTime: 0,
          totalThumbnailLoadTime: 0,
        };
      }
      byConnectionType[type].count++;
      byConnectionType[type].totalLoadTime += metric.totalLoadTime;
      byConnectionType[type].totalThumbnailLoadTime += metric.thumbnailLoadTime;
    });

    Object.keys(byConnectionType).forEach((type) => {
      byConnectionType[type].averageLoadTime =
        byConnectionType[type].totalLoadTime / byConnectionType[type].count;
      byConnectionType[type].averageThumbnailLoadTime =
        byConnectionType[type].totalThumbnailLoadTime /
        byConnectionType[type].count;
      delete byConnectionType[type].totalLoadTime;
      delete byConnectionType[type].totalThumbnailLoadTime;
    });

    // Group by device type
    const byDeviceType: Record<string, any> = {};
    this.metrics.forEach((metric) => {
      const type = metric.deviceType;
      if (!byDeviceType[type]) {
        byDeviceType[type] = { count: 0, totalLoadTime: 0 };
      }
      byDeviceType[type].count++;
      byDeviceType[type].totalLoadTime += metric.totalLoadTime;
    });

    Object.keys(byDeviceType).forEach((type) => {
      byDeviceType[type].averageLoadTime =
        byDeviceType[type].totalLoadTime / byDeviceType[type].count;
      delete byDeviceType[type].totalLoadTime;
    });

    // Group by thumbnail quality
    const byQuality: Record<string, any> = {};
    this.metrics.forEach((metric) => {
      const quality = metric.thumbnailQuality;
      if (!byQuality[quality]) {
        byQuality[quality] = { count: 0, totalLoadTime: 0 };
      }
      byQuality[quality].count++;
      byQuality[quality].totalLoadTime += metric.totalLoadTime;
    });

    Object.keys(byQuality).forEach((quality) => {
      byQuality[quality].averageLoadTime =
        byQuality[quality].totalLoadTime / byQuality[quality].count;
      delete byQuality[quality].totalLoadTime;
    });

    return {
      summary: {
        totalVideos,
        averageLoadTime,
        averageThumbnailLoadTime,
        averageIframeLoadTime,
        cacheHitRate,
        errorRate,
      },
      byConnectionType,
      byDeviceType,
      byQuality,
      recentMetrics: this.metrics.slice(-10), // Last 10 metrics
    };
  }

  /**
   * Get performance comparison (before vs after optimization)
   */
  getPerformanceComparison(): {
    before: { averageLoadTime: number; cacheHitRate: number };
    after: { averageLoadTime: number; cacheHitRate: number };
    improvement: { loadTime: number; cacheHitRate: number };
  } {
    const optimizedMetrics = this.metrics.filter((m) => m.optimizationEnabled);
    const unoptimizedMetrics = this.metrics.filter(
      (m) => !m.optimizationEnabled,
    );

    const before = {
      averageLoadTime:
        unoptimizedMetrics.length > 0
          ? unoptimizedMetrics.reduce((sum, m) => sum + m.totalLoadTime, 0) /
            unoptimizedMetrics.length
          : 0,
      cacheHitRate:
        unoptimizedMetrics.length > 0
          ? unoptimizedMetrics.filter((m) => m.cacheHit).length /
            unoptimizedMetrics.length
          : 0,
    };

    const after = {
      averageLoadTime:
        optimizedMetrics.length > 0
          ? optimizedMetrics.reduce((sum, m) => sum + m.totalLoadTime, 0) /
            optimizedMetrics.length
          : 0,
      cacheHitRate:
        optimizedMetrics.length > 0
          ? optimizedMetrics.filter((m) => m.cacheHit).length /
            optimizedMetrics.length
          : 0,
    };

    const improvement = {
      loadTime:
        before.averageLoadTime > 0
          ? ((before.averageLoadTime - after.averageLoadTime) /
              before.averageLoadTime) *
            100
          : 0,
      cacheHitRate: after.cacheHitRate - before.cacheHitRate,
    };

    return { before, after, improvement };
  }

  /**
   * Export metrics for external analysis
   */
  exportMetrics(): VideoPerformanceMetrics[] {
    return [...this.metrics];
  }

  /**
   * Clear all metrics
   */
  clearMetrics(): void {
    this.metrics = [];
  }

  /**
   * Send metrics to analytics endpoint
   */
  private async sendToAnalytics(
    metrics: VideoPerformanceMetrics,
  ): Promise<void> {
    if (!this.analyticsEndpoint) return;

    try {
      await fetch(this.analyticsEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(metrics),
      });
    } catch (error) {
      console.warn("Failed to send metrics to analytics endpoint:", error);
    }
  }

  /**
   * Generate performance insights
   */
  getPerformanceInsights(): string[] {
    const report = this.getPerformanceReport();
    const insights: string[] = [];

    if (report.summary.totalVideos === 0) {
      return ["No video performance data available yet"];
    }

    // Load time insights
    if (report.summary.averageLoadTime > 1000) {
      insights.push(
        "⚠️ Average video load time is high (>1s). Consider optimizing thumbnails or connection.",
      );
    } else if (report.summary.averageLoadTime < 500) {
      insights.push("✅ Excellent video load performance!");
    }

    // Cache insights
    if (report.summary.cacheHitRate < 0.3) {
      insights.push(
        "💡 Low cache hit rate. Consider increasing cache TTL or preloading more videos.",
      );
    } else if (report.summary.cacheHitRate > 0.7) {
      insights.push("✅ High cache efficiency!");
    }

    // Connection type insights
    Object.entries(report.byConnectionType).forEach(([type, data]) => {
      if (data.averageLoadTime > 1500) {
        insights.push(
          `🐌 Slow loading on ${type} connections. Consider reducing quality for this connection type.`,
        );
      }
    });

    // Error insights
    if (report.summary.errorRate > 0.1) {
      insights.push(
        "🚨 High error rate detected. Check network connectivity and video availability.",
      );
    }

    return insights;
  }
}

/**
 * Video performance tracker for individual videos
 */
export class VideoPerformanceTracker {
  private videoId: string;
  private startTime: number;
  private thumbnailStartTime: number;
  private iframeStartTime: number;
  private errors: string[] = [];
  private analytics: VideoPerformanceAnalytics;

  constructor(videoId: string) {
    this.videoId = videoId;
    this.analytics = VideoPerformanceAnalytics.getInstance();
    this.startTime = performance.now();
  }

  /**
   * Start thumbnail loading tracking
   */
  startThumbnailLoading(): void {
    this.thumbnailStartTime = performance.now();
  }

  /**
   * Record thumbnail loaded
   */
  thumbnailLoaded(): number {
    const loadTime = performance.now() - this.thumbnailStartTime;
    console.log(
      `📹 Thumbnail loaded for ${this.videoId} in ${loadTime.toFixed(2)}ms`,
    );
    return loadTime;
  }

  /**
   * Start iframe loading tracking
   */
  startIframeLoading(): void {
    this.iframeStartTime = performance.now();
  }

  /**
   * Record iframe loaded
   */
  iframeLoaded(): number {
    const loadTime = performance.now() - this.iframeStartTime;
    console.log(
      `📹 Iframe loaded for ${this.videoId} in ${loadTime.toFixed(2)}ms`,
    );
    return loadTime;
  }

  /**
   * Record error
   */
  recordError(error: string): void {
    this.errors.push(error);
    console.error(`❌ Video error for ${this.videoId}:`, error);
  }

  /**
   * Complete tracking and record metrics
   */
  complete(
    thumbnailLoadTime: number,
    iframeLoadTime: number,
    options: {
      thumbnailQuality: string;
      cacheHit: boolean;
      optimizationEnabled: boolean;
    },
  ): void {
    const totalLoadTime = performance.now() - this.startTime;

    // Get connection info
    const connectionInfo = this.getConnectionInfo();
    const deviceInfo = this.getDeviceInfo();

    this.analytics.recordMetrics({
      videoId: this.videoId,
      loadStartTime: this.startTime,
      thumbnailLoadTime,
      iframeLoadTime,
      totalLoadTime,
      connectionType: connectionInfo.effectiveType,
      connectionSpeed: connectionInfo.downlink,
      thumbnailQuality: options.thumbnailQuality,
      cacheHit: options.cacheHit,
      userAgent:
        typeof navigator !== "undefined" ? navigator.userAgent : "Node.js",
      viewportSize: {
        width: typeof window !== "undefined" ? window.innerWidth : 1920,
        height: typeof window !== "undefined" ? window.innerHeight : 1080,
      },
      deviceType: deviceInfo.type,
      optimizationEnabled: options.optimizationEnabled,
      errors: this.errors,
    });

    console.log(`📊 Video ${this.videoId} performance recorded:`, {
      totalLoadTime: `${totalLoadTime.toFixed(2)}ms`,
      thumbnailLoadTime: `${thumbnailLoadTime.toFixed(2)}ms`,
      iframeLoadTime: `${iframeLoadTime.toFixed(2)}ms`,
      connectionType: connectionInfo.effectiveType,
      cacheHit: options.cacheHit,
    });
  }

  private getConnectionInfo() {
    // Check if we're in a browser environment
    if (typeof navigator !== "undefined" && "connection" in navigator) {
      const connection = (navigator as any).connection;
      return {
        effectiveType: connection.effectiveType || "unknown",
        downlink: connection.downlink || 0,
        rtt: connection.rtt || 0,
        saveData: connection.saveData || false,
      };
    }
    return {
      effectiveType: "unknown",
      downlink: 0,
      rtt: 0,
      saveData: false,
    };
  }

  private getDeviceInfo() {
    // Check if we're in a browser environment
    if (typeof window !== "undefined" && typeof navigator !== "undefined") {
      const width = window.innerWidth;

      if (width < 768) {
        return { type: "mobile" as const };
      } else if (width < 1024) {
        return { type: "tablet" as const };
      } else {
        return { type: "desktop" as const };
      }
    }
    return { type: "desktop" as const };
  }
}

/**
 * Performance dashboard utilities
 */
export function generatePerformanceDashboard(): string {
  const analytics = VideoPerformanceAnalytics.getInstance();
  const report = analytics.getPerformanceReport();
  const comparison = analytics.getPerformanceComparison();
  const insights = analytics.getPerformanceInsights();

  return `
📊 Video Performance Dashboard
==============================

📈 Summary
----------
Total Videos: ${report.summary.totalVideos}
Average Load Time: ${report.summary.averageLoadTime.toFixed(2)}ms
Average Thumbnail Load Time: ${report.summary.averageThumbnailLoadTime.toFixed(2)}ms
Average Iframe Load Time: ${report.summary.averageIframeLoadTime.toFixed(2)}ms
Cache Hit Rate: ${(report.summary.cacheHitRate * 100).toFixed(1)}%
Error Rate: ${(report.summary.errorRate * 100).toFixed(1)}%

🔄 Performance Comparison
-------------------------
Before Optimization:
  - Average Load Time: ${comparison.before.averageLoadTime.toFixed(2)}ms
  - Cache Hit Rate: ${(comparison.before.cacheHitRate * 100).toFixed(1)}%

After Optimization:
  - Average Load Time: ${comparison.after.averageLoadTime.toFixed(2)}ms
  - Cache Hit Rate: ${(comparison.after.cacheHitRate * 100).toFixed(1)}%

Improvement:
  - Load Time: ${comparison.improvement.loadTime.toFixed(1)}% faster
  - Cache Hit Rate: ${(comparison.improvement.cacheHitRate * 100).toFixed(1)}% better

💡 Insights
-----------
${insights.map((insight) => `• ${insight}`).join("\n")}
  `;
}

/**
 * Initialize video performance analytics
 */
export function initializeVideoPerformanceAnalytics(endpoint?: string): void {
  const analytics = VideoPerformanceAnalytics.getInstance();

  if (endpoint) {
    analytics.setAnalyticsEndpoint(endpoint);
  }

  // Log initial setup
  console.log("📊 Video Performance Analytics initialized");
  console.log("🎯 Analytics Features:");
  console.log("   • Real-time performance tracking");
  console.log("   • Connection-aware analytics");
  console.log("   • Cache hit rate monitoring");
  console.log("   • Before/after optimization comparison");
  console.log("   • Performance insights and recommendations");
  console.log("   • Periodic reporting every 60 seconds");

  // Set up periodic reporting
  setInterval(() => {
    const report = analytics.getPerformanceReport();
    if (report.summary.totalVideos > 0) {
      console.log("📊 Performance Report:", {
        averageThumbnailLoadTime: `${report.summary.averageThumbnailLoadTime.toFixed(2)}ms`,
        averageIframeLoadTime: `${report.summary.averageIframeLoadTime.toFixed(2)}ms`,
        errorRate: `${(report.summary.errorRate * 100).toFixed(1)}%`,
        connectionTypes: Object.keys(report.byConnectionType),
        deviceTypes: Object.keys(report.byDeviceType),
      });
    }
  }, 60000); // Report every minute
}
